import React, { useState } from 'react';
import {
  CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp,
  ArrowRight, BarChart3,
} from 'lucide-react';
import { calculateReadiness } from './readiness';
import type { ReadinessResult, ReadinessCheck } from './readiness';
import type { WizardState } from './types';
import { STEP_LABELS } from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ReadinessDashboardProps {
  state: WizardState;
  onEditStep?: (step: number) => void;
  tenantName?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function TierBadge({ tier }: { tier: ReadinessResult['tier'] }) {
  if (tier === 'ready') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">
        <CheckCircle2 className="h-3.5 w-3.5" /> Ready to Provision
      </span>
    );
  }
  if (tier === 'partial') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700">
        <AlertTriangle className="h-3.5 w-3.5" /> Partially Complete
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700">
      <XCircle className="h-3.5 w-3.5" /> Needs Attention
    </span>
  );
}

function ScoreRing({ score }: { score: number }) {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score === 100 ? '#16a34a' : score >= 60 ? '#d97706' : '#dc2626';

  return (
    <div className="relative flex items-center justify-center w-24 h-24">
      <svg className="absolute inset-0 -rotate-90" width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={radius} fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle
          cx="48" cy="48" r={radius}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <span className="text-xl font-bold" style={{ color }}>{score}%</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step group component
// ---------------------------------------------------------------------------

function StepGroup({
  step,
  label,
  checks,
  onEdit,
}: {
  step: number;
  label: string;
  checks: ReadinessCheck[];
  onEdit?: (step: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const passed = checks.filter((c) => c.passed).length;
  const total = checks.length;
  const allPassed = passed === total;
  const hasFailures = passed < total;

  return (
    <div className={`rounded-lg border ${allPassed ? 'border-green-200 bg-green-50' : hasFailures ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <div className="flex items-center gap-3">
          <span className={`flex items-center justify-center h-6 w-6 rounded-full text-xs font-bold shrink-0 ${allPassed ? 'bg-green-500 text-white' : hasFailures ? 'bg-red-400 text-white' : 'bg-gray-300 text-gray-600'}`}>
            {step}
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">{label}</p>
            <p className="text-xs text-gray-500">{passed}/{total} checks passed</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!allPassed && onEdit && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(step); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
            >
              Edit <ArrowRight className="h-3 w-3" />
            </button>
          )}
          {expanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-1.5 border-t border-gray-200">
          {checks.map((c) => (
            <div key={c.id} className="flex items-center gap-2">
              {c.passed
                ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                : <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />}
              <span className={`text-xs ${c.passed ? 'text-gray-600' : 'text-red-700'}`}>{c.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReadinessDashboard
// ---------------------------------------------------------------------------

export default function ReadinessDashboard({ state, onEditStep, tenantName }: ReadinessDashboardProps) {
  const result = calculateReadiness(state);

  const checksByStep = STEP_LABELS.reduce<Record<number, ReadinessCheck[]>>((acc, _, idx) => {
    const step = idx + 1;
    acc[step] = result.checks.filter((c) => c.step === step);
    return acc;
  }, {});

  const failedChecks = result.checks.filter((c) => !c.passed);

  return (
    <div className="space-y-6">
      {/* Summary card */}
      <div className="flex items-center gap-6 p-5 rounded-xl border border-gray-200 bg-white shadow-sm">
        <ScoreRing score={result.score} />
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-lg font-bold text-gray-900">
              {tenantName ? `${tenantName} — Readiness` : 'Onboarding Readiness'}
            </h2>
            <TierBadge tier={result.tier} />
          </div>
          <p className="text-sm text-gray-500 mb-3">
            {result.passed} of {result.total} checks passed
          </p>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${result.score === 100 ? 'bg-green-500' : result.score >= 60 ? 'bg-amber-400' : 'bg-red-400'}`}
              style={{ width: `${result.score}%` }}
            />
          </div>
        </div>
      </div>

      {/* Failed checks summary */}
      {failedChecks.length > 0 && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <h3 className="text-sm font-semibold text-red-700">{failedChecks.length} item{failedChecks.length !== 1 ? 's' : ''} need attention</h3>
          </div>
          <ul className="space-y-1">
            {failedChecks.map((c) => (
              <li key={c.id} className="flex items-center gap-2 text-xs text-red-600">
                <XCircle className="h-3 w-3 shrink-0" />
                {c.label} <span className="text-red-400">(Step {c.step}: {STEP_LABELS[c.step - 1]})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Per-step breakdown */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="h-4 w-4 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700">Step-by-Step Breakdown</h3>
        </div>
        <div className="space-y-2">
          {STEP_LABELS.map((label, idx) => {
            const step = idx + 1;
            const checks = checksByStep[step] ?? [];
            if (checks.length === 0) return null;
            return (
              <StepGroup
                key={step}
                step={step}
                label={label}
                checks={checks}
                onEdit={onEditStep}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}
